import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";

interface WelcomeModalProps {
  open: boolean;
  onStart: () => void;
  onSkip: () => void;
  agentName?: string;
}

export function WelcomeModal({ open, onStart, onSkip, agentName = "Agente" }: WelcomeModalProps) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-background/80 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          />

          {/* Card */}
          <motion.div
            className="relative z-10 w-full max-w-lg rounded-3xl border border-border/50 bg-card p-8 shadow-2xl overflow-hidden"
            initial={{ scale: 0.85, opacity: 0, y: 40 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            {/* Gradient accent top */}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary to-secondary" />

            {/* Decorative glow */}
            <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-secondary/15 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-primary/10 blur-3xl pointer-events-none" />

            <div className="relative text-center">
              {/* Icon */}
              <motion.div
                className="mx-auto mb-6 h-20 w-20 rounded-3xl bg-gradient-to-br from-primary/20 to-secondary/20 border border-primary/20 flex items-center justify-center"
                initial={{ rotate: -10 }}
                animate={{ rotate: 0 }}
                transition={{ delay: 0.3, type: "spring" }}
              >
                <PartyPopper className="h-10 w-10 text-primary" />
              </motion.div>

              {/* Floating confetti dots */}
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 rounded-full"
                  style={{
                    backgroundColor: `hsl(var(--${["primary", "secondary", "tertiary"][i % 3]}))`,
                    left: `${15 + i * 14}%`,
                    top: `${10 + (i % 2) * 8}%`,
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: [0, 0.6, 0], y: [-5, -25, -5] }}
                  transition={{ delay: 0.5 + i * 0.1, duration: 2, repeat: Infinity }}
                />
              ))}

              <motion.h2
                className="font-heading text-3xl font-bold text-foreground mb-3"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                Bem-vindo(a),{" "}
                <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  {agentName}!
                </span>
              </motion.h2>

              <motion.p
                className="text-muted-foreground leading-relaxed mb-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.35 }}
              >
                Sua conta foi criada com sucesso! 🎉
              </motion.p>
              <motion.p
                className="text-sm text-muted-foreground leading-relaxed mb-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.45 }}
              >
                Vamos fazer um tour rápido pelo sistema para que você conheça as
                principais funcionalidades e comece a fazer a diferença na sua cidade.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55 }}
              >
                <Button
                  size="lg"
                  className="gap-2 px-8 h-13 rounded-full bg-gradient-to-r from-primary via-secondary to-primary shadow-lg hover:shadow-xl transition-shadow"
                  onClick={onStart}
                >
                  <Sparkles className="h-5 w-5" />
                  Iniciar Tour
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </motion.div>

              <motion.button
                className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                onClick={onSkip}
              >
                Pular tutorial
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
